// ───────────────────────────────────────────────────────────────────────────
// 🔑 THE MLO OF RECORD (Jesse, 2026-10-31 — push B; backend utils/mloOfRecord, routes/mloOfRecord).
//   MloOfRecordLine — one small line on the DoorlyDesk file header: the MLO of record and NMLS ID, since when, and on a
//     partner LO's file the originator. Allow-listed staff (Hannah, Jesse) can pick another licensed MLO, with a reason;
//     an MLO without an active licence in the property state is refused by the server with the reason shown.
//   MloBulkAssignCard — Team Members: how many open applications have no MLO of record, and (allow-listed) assign them
//     all, effective today, each to the next eligible loan officer (2026-11-01: by role and licence, round robin — no
//     state default). Never backdated: neither control takes a date. Links to the MLO licences page (M1).
// ───────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' }) : '');

export function MloOfRecordLine({ userId, token }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(() => axios.get(`${API}/api/admin/borrowers/${userId}/mlo-of-record`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setInfo(r.data)).catch(() => setInfo(null)), [userId, token]);
  useEffect(() => { if (userId) load(); }, [userId, load]);
  if (!info || !info.available) return null;
  const cur = info.current;

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await axios.post(`${API}/api/admin/borrowers/${userId}/mlo-of-record`, { nmls_id: pick, reason }, { headers });
      setOpen(false); setPick(''); setReason(''); load();
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.response?.data?.error || 'That did not go through.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="mlo-of-record" className="flex flex-wrap items-center gap-2 mt-2 text-xs">
      <span className="font-semibold text-doorly-slate">MLO of record:</span>
      {cur ? (
        <span data-testid="mlo-of-record-name" className="font-semibold text-doorly-navy">
          {cur.mloName} · NMLS {cur.mloNmlsId}
          <span className="font-normal text-doorly-slate"> · {cur.propertyState} · since {fmtDate(cur.effectiveAt)}</span>
        </span>
      ) : (
        <span data-testid="mlo-of-record-none" className="font-semibold text-doorly-orange">None assigned</span>
      )}
      {cur && cur.originator && cur.originator.kind === 'partner' && (
        <span data-testid="mlo-of-record-originator" className="text-doorly-slate">
          · Originator: <span className="font-semibold text-doorly-blue">{cur.originator.name || 'partner LO'}{cur.originator.nmlsId ? ` · NMLS ${cur.originator.nmlsId}` : ''}</span>
        </span>
      )}
      {info.canAssign && !open && (
        <button type="button" data-testid="mlo-of-record-change" onClick={() => setOpen(true)}
          className="px-2 py-0.5 rounded border border-doorly-neutral-light text-doorly-blue">{cur ? 'Change' : 'Assign'}</button>
      )}
      {open && (
        <span className="flex flex-wrap items-center gap-2">
          <select data-testid="mlo-of-record-pick" value={pick} onChange={(e) => setPick(e.target.value)}
            className="border border-doorly-neutral-light rounded px-1 py-0.5 text-xs">
            <option value="">Pick an MLO…</option>
            {(info.choices || []).map((c) => (
              <option key={c.nmlsId} value={c.nmlsId}>{c.name} · NMLS {c.nmlsId}{c.licensedHere ? '' : ` (not licensed in ${info.propertyState})`}</option>
            ))}
          </select>
          <input data-testid="mlo-of-record-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (recorded)"
            className="border border-doorly-neutral-light rounded px-1 py-0.5 text-xs" />
          <button type="button" data-testid="mlo-of-record-save" disabled={busy || !pick || reason.trim().length < 5} onClick={save}
            className="px-2 py-0.5 rounded text-white bg-doorly-blue disabled:bg-doorly-neutral-light">Assign from today</button>
          <button type="button" onClick={() => { setOpen(false); setMsg(null); }} className="px-2 py-0.5 rounded border border-doorly-neutral-light">Cancel</button>
        </span>
      )}
      {msg && <span data-testid="mlo-of-record-error" className="text-doorly-coral font-semibold">{msg}</span>}
    </div>
  );
}

export function MloBulkAssignCard({ token }) {
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = useCallback(() => axios.get(`${API}/api/admin/mlo-of-record/open-without`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setInfo(r.data)).catch(() => setInfo(null)), [token]);
  useEffect(() => { load(); }, [load]);
  if (!info || !info.available) return null;

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/mlo-of-record/bulk-assign`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(`${r.data.assigned} assigned from today; ${r.data.refused} refused (no eligible loan officer licensed in the state — see Alerts).`);
      setReason(''); load();
    } catch (e) {
      setResult(e?.response?.data?.message || e?.response?.data?.error || 'That did not go through.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="mlo-bulk-assign" className="bg-white rounded-xl border border-doorly-neutral-light p-4 mb-4">
      <div className="text-sm font-semibold text-doorly-navy mb-1">MLO of record</div>
      <div className="text-xs text-doorly-slate mb-2">
        <span data-testid="mlo-bulk-open">{info.open}</span> open application{info.open === 1 ? '' : 's'} with no MLO of record.
        {' '}Each gets the next eligible loan officer licensed in its property state, effective today — never backdated.
        {' '}<a href="/admin/settings/mlo-licences" data-testid="mlo-licences-link" className="text-doorly-blue font-semibold">MLO licences →</a>
      </div>
      {info.canAssign && info.open > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <input data-testid="mlo-bulk-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (recorded with your name)"
            className="border border-doorly-neutral-light rounded-lg px-2 py-1 text-sm" />
          <button type="button" data-testid="mlo-bulk-run" disabled={busy || reason.trim().length < 5} onClick={run}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-doorly-blue disabled:bg-doorly-neutral-light">Assign all</button>
        </div>
      )}
      {!info.canAssign && info.open > 0 && <div className="text-xs text-doorly-slate">Only Hannah and Jesse can assign them.</div>}
      {result && <div data-testid="mlo-bulk-result" className="text-xs text-doorly-navy mt-2">{result}</div>}
    </div>
  );
}
